// File: src/modules/jobs/JobDetails.js

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchJobById, updateJobStatus, saveJobCosts, updateJobCost, deleteJobCost } from './jobsService';
import { getCostItems, createCostItem } from '../cost-items/costItemsService';
import styled from 'styled-components';
import Input from '../../components/shared/Input';
import Select from '../../components/shared/Select';
import Modal from '../../components/shared/Modal';
import {
  Container,
  Header,
  Title,
  CardWrapper,
  Card,
  CardTitle,
  CardContent,
  TabWrapper,
  TabButton,
  Button,
} from './JobStyles';
import DashboardLayout from '../../layouts/DashboardLayout';
import SmsModal from '../leads/SmsModal';
import { useAuth } from '../auth/AuthContext';
import { FaArrowLeft, FaEdit, FaPlus, FaSave, FaTimes, FaTrash, FaCommentDots } from 'react-icons/fa';
import { formatCurrency } from '../../utils/utils';
import Map from '../../components/shared/Map';
import { calculateDistance } from '../../utils/distanceUtils';
import { getEstimateById, getServices, getSuppliersByLocation } from '../estimates/EstimateService';
import WeatherForecast from '../../components/weather/WeatherForecast';
import ChangeOrderListSection from '../change-orders/ChangeOrderListSection';
import CostBreakdown from './CostBreakdown';

const CostSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const SummaryCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: 16px;
  text-align: center;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  border-top: 3px solid ${props => props.accent || props.theme.colors.primary};
`;

const SummaryLabel = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.color || props.theme.colors.textPrimary};
  margin-top: 4px;
`;

const ExistingCostCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: 16px;
  margin-bottom: 10px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  border-left: 3px solid ${props => props.theme.colors.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const CostCardInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CostCardTitle = styled.div`
  font-weight: 700;
  font-size: 15px;
  color: ${props => props.theme.colors.textPrimary};
`;

const CostCardSub = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 2px;
`;

const CostCardAmount = styled.div`
  font-weight: 700;
  font-size: 16px;
  color: ${props => props.theme.colors.textPrimary};
  text-align: right;
`;

const CostActionBtns = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
`;

const CostIconBtn = styled.button`
  background: none;
  border: none;
  color: ${props => props.color || props.theme.colors.textSecondary};
  cursor: pointer;
  padding: 8px;
  font-size: 14px;
  border-radius: 50%;
  &:hover { background: ${props => props.color || props.theme.colors.textSecondary}15; }
`;

const AddCostSection = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: 20px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  margin-top: 16px;
  margin-bottom: 8px;
`;

const CostEntryCard = styled.div`
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid ${props => props.theme.colors.inputGray};
`;

const CostEntryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const CostEntryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const CostEntryNum = styled.span`
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.textPrimary};
`;

const RemoveCostBtn = styled.button`
  background: none;
  border: 1px solid ${props => props.theme.colors.error};
  color: ${props => props.theme.colors.error};
  padding: 6px 10px;
  border-radius: ${props => props.theme.borderRadius};
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover { background: ${props => props.theme.colors.error}15; }
`;

const CostEntryTotal = styled.div`
  text-align: right;
  font-weight: 700;
  font-size: 15px;
  color: ${props => props.theme.colors.primary};
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${props => props.theme.colors.inputGray};
`;

const ActionBtnRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const ModeToggleRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const ModeToggleBtn = styled.button`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${props => props.active ? props.theme.colors.primary : props.theme.colors.inputGray};
  background: ${props => props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.active ? '#fff' : props.theme.colors.textPrimary};
  border-radius: ${props => props.theme.borderRadius};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { opacity: 0.9; }
`;

const ReuseToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 10px;
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  font-size: 13px;
  color: ${props => props.theme.colors.textPrimary};
  flex-wrap: wrap;
  input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; }
`;

const InlineNameInput = styled.input`
  flex: 1;
  min-width: 160px;
  padding: 6px 8px;
  border: 1px solid ${props => props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  font-size: 13px;
`;

const PickerHint = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: -4px;
  margin-bottom: 8px;
`;

const MapWrapper = styled.div`
    width: 100%;
    height: 400px;
    margin-top: ${(props) => props.theme.spacing(4)};
`;

const SupplierDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const SupplierItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${(props) => props.theme.spacing(3)};
  border-bottom: 1px solid ${(props) => props.theme.colors.inputGray};
  &:last-child {
    border-bottom: none;
  }
`;

const SupplierName = styled.span`
  font-weight: bold;
  font-size: 18px;
  color: ${(props) => props.theme.colors.primaryText};
`;

const SupplierDistance = styled.span`
  font-size: 16px;
  color: ${(props) => props.theme.colors.textSecondary};
`;

const SupplierCategories = styled.div`
  font-size: 14px;
  color: ${(props) => props.theme.colors.textSecondary};
  margin-top: ${(props) => props.theme.spacing(1)};
`;

const JobDetails = () => {
  const { job_id } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [showCostForm, setShowCostForm] = useState(false);
  const [costs, setCosts] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [costToDelete, setCostToDelete] = useState(null);
  const [editingCost, setEditingCost] = useState(null);
  const history = useNavigate();
    const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);

  const [suppliers, setSuppliers] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [services, setServices] = useState([]);
  const [costItems, setCostItems] = useState([]);

  const supplierPinUrl = 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'; // Blue pin for suppliers
  const jobSitePinUrl = 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'; // Red pin for job site

  const loadJob = async () => {
    try {
      const data = await fetchJobById(job_id);
      setJob(data);
      const [lat, lng] = data.geolocation ? data.geolocation.split(';') : [null, null];
      setLatitude(parseFloat(lat));
      setLongitude(parseFloat(lng));

      if (data.status === 'Completed') {
        setShowCostForm(true);
      } else {
        setShowCostForm(true);
      }
      fetchSuppliers(lat, lng);

      if (data.estimate_id) {
        try {
          const estRes = await getEstimateById(data.estimate_id);
          setEstimate(estRes.data);
        } catch (e) {
          console.error('Error loading estimate for job:', e);
        }
      }
    } catch (err) {
      console.error('Error fetching job details:', err);
    }
  };

  const loadCostItems = async () => {
    try {
      const data = await getCostItems({ active: true });
      setCostItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading cost items:', err);
      setCostItems([]);
    }
  };

  useEffect(() => {
    loadJob();
    getServices().then((res) => setServices(res.data || [])).catch(() => {});
    loadCostItems();
  }, [job_id]);

  const handleStatusUpdate = async (newStatus) => {
    try {
      await updateJobStatus(job_id, newStatus);
      setJob({ ...job, status: newStatus });
      // if (newStatus === 'Completed') {
      //   setShowCostForm(true); // Show cost form if job is completed
      // }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleAddCost = () => {
    setCosts([
      ...costs,
      {
        category: '',
        description: '',
        quantity: '',
        unit_price: '',
        entryMode: costItems.length > 0 ? 'pick' : 'manual',
        cost_item_id: '',
        saveAsTemplate: true,
        template_name: '',
        unit: '',
      },
    ]);
  };

  const handleCostChange = (index, field, value) => {
    const newCosts = [...costs];
    newCosts[index][field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      const quantity = parseFloat(newCosts[index].quantity) || 0;
      const unitPrice = parseFloat(newCosts[index].unit_price) || 0;
      newCosts[index].total_cost = (quantity * unitPrice).toFixed(2);
    }
    setCosts(newCosts);
  };

  const handleSetEntryMode = (index, mode) => {
    const newCosts = [...costs];
    newCosts[index].entryMode = mode;
    if (mode === 'pick') {
      newCosts[index].saveAsTemplate = false;
    } else {
      newCosts[index].cost_item_id = '';
      if (newCosts[index].saveAsTemplate === undefined) {
        newCosts[index].saveAsTemplate = true;
      }
    }
    setCosts(newCosts);
  };

  const handlePickCostItem = (index, itemId) => {
    const item = costItems.find((it) => it.cost_item_id === itemId);
    const newCosts = [...costs];
    if (!item) {
      newCosts[index].cost_item_id = '';
      setCosts(newCosts);
      return;
    }
    newCosts[index].cost_item_id = itemId;
    newCosts[index].category = item.category;
    newCosts[index].description = item.unit ? `${item.name} (${item.unit})` : item.name;
    newCosts[index].unit_price = item.default_unit_price != null ? String(item.default_unit_price) : '';
    newCosts[index].unit = item.unit || '';
    const qty = parseFloat(newCosts[index].quantity) || 0;
    const price = parseFloat(newCosts[index].unit_price) || 0;
    newCosts[index].total_cost = (qty * price).toFixed(2);
    setCosts(newCosts);
  };

  const handleRemoveCost = (index) => {
    const newCosts = costs.filter((_, i) => i !== index);
    setCosts(newCosts);
  };

  const autoSaveTemplatesFor = async (entries) => {
    let didCreateAny = false;
    for (const c of entries) {
      if (c.entryMode !== 'manual' || !c.saveAsTemplate) continue;
      const name = (c.template_name || c.description || '').trim();
      if (!name || !c.category || c.unit_price === '' || c.unit_price == null) continue;
      try {
        await createCostItem({
          name,
          category: c.category,
          unit: c.unit || null,
          default_unit_price: parseFloat(c.unit_price) || 0,
          description: null,
        });
        didCreateAny = true;
      } catch (err) {
        console.error('Error auto-saving cost item template:', err);
      }
    }
    if (didCreateAny) {
      await loadCostItems();
    }
  };

  const handleSaveCosts = async () => {
    try {
      await autoSaveTemplatesFor(costs);
      const payload = costs.map((c) => ({
        category: c.category,
        description: c.description,
        quantity: c.quantity,
        unit_price: c.unit_price,
      }));
      const response = await saveJobCosts(job_id, payload);
      setJob({ ...job, costs: [...(job.costs || []), ...response] });
      setCosts([]);
    } catch (error) {
      console.error('Error saving costs:', error);
    }
  };

  const handleEditCost = (cost) => {
    setEditingCost({
      ...cost,
      category: cost.category,
      description: cost.description,
      quantity: cost.quantity,
      unit_price: cost.unit_price,
      entryMode: 'manual',
      cost_item_id: '',
      saveAsTemplate: false,
      template_name: cost.description || '',
      unit: '',
    });
  };

  const handleEditCostChange = (field, value) => {
    setEditingCost(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        const qty = parseFloat(updated.quantity) || 0;
        const price = parseFloat(updated.unit_price) || 0;
        updated.total_cost = (qty * price).toFixed(2);
      }
      return updated;
    });
  };

  const handleEditPickCostItem = (itemId) => {
    const item = costItems.find((it) => it.cost_item_id === itemId);
    setEditingCost((prev) => {
      if (!prev) return prev;
      if (!item) return { ...prev, cost_item_id: '' };
      const updated = {
        ...prev,
        cost_item_id: itemId,
        category: item.category,
        description: item.unit ? `${item.name} (${item.unit})` : item.name,
        unit_price: item.default_unit_price != null ? String(item.default_unit_price) : '',
        unit: item.unit || '',
      };
      const qty = parseFloat(updated.quantity) || 0;
      const price = parseFloat(updated.unit_price) || 0;
      updated.total_cost = (qty * price).toFixed(2);
      return updated;
    });
  };

  const handleSetEditMode = (mode) => {
    setEditingCost((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, entryMode: mode };
      if (mode === 'pick') {
        updated.saveAsTemplate = false;
      } else {
        updated.cost_item_id = '';
        if (updated.saveAsTemplate === undefined) updated.saveAsTemplate = true;
      }
      return updated;
    });
  };

  const handleSaveEditCost = async () => {
    try {
      await autoSaveTemplatesFor([editingCost]);
      const updated = await updateJobCost(job_id, editingCost.cost_id, {
        category: editingCost.category,
        description: editingCost.description,
        quantity: editingCost.quantity,
        unit_price: editingCost.unit_price
      });
      const updatedCosts = job.costs.map(c => c.cost_id === editingCost.cost_id ? updated : c);
      setJob({ ...job, costs: updatedCosts });
      setEditingCost(null);
    } catch (error) {
      console.error('Error updating cost:', error);
    }
  };

  const handleDeleteCost = (cost) => {
    setCostToDelete(cost);
    setDeleteModalOpen(true);
  };

  const confirmDeleteCost = async () => {
    try {
      await deleteJobCost(job_id, costToDelete.cost_id);
      const updatedCosts = job.costs.filter(cost => cost.cost_id !== costToDelete.cost_id);
      setJob({ ...job, costs: updatedCosts });
      setDeleteModalOpen(false);
      setCostToDelete(null);
    } catch (error) {
      console.error('Error deleting cost:', error);
      alert('Error deleting cost');
    }
  };

  const calculateTotalCost = () => {
    return job.costs.reduce((total, cost) => total + parseFloat(cost.total_cost), 0).toFixed(2);
  };

  const calculateProfit = () => {
    return 10;
    // return parseFloat(job.total_price) - calculateTotalCost();
  }

  console.log("Job Data: ", job);
  console.log(calculateProfit());

  const fetchSuppliers = async (lat, lng) => {
    try {
      // Fetch suppliers from the API based on location
      const response = await getSuppliersByLocation(lat, lng, 20);
      const fetchedSuppliers = response.data;

      const suppliersWithCategories = fetchedSuppliers.map((supplier) => ({
        ...supplier,
        distance: calculateDistance(
          lat,
          lng,
          supplier.latitude,
          supplier.longitude
        ).toFixed(2),
        // Ensure categories are properly formatted
        categories: supplier.categories || [], // Default to an empty array if undefined
      }));

      // Sort suppliers by distance (closest to farthest)
      const sortedSuppliers = suppliersWithCategories.sort(
        (a, b) => a.distance - b.distance
      );
      // Update the state with sorted suppliers
      setSuppliers(sortedSuppliers);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const formattedSuppliers = suppliers.map((supplier) => ({
    lat: supplier.latitude,
    lng: supplier.longitude,
    title: supplier.name,
    distance: supplier.distance,
    products: supplier.categories.map((category) => category.name),
    isSupplier: true,
  }));

  if (!job) return <div>Loading job details...</div>;

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <Button onClick={() => history(-1)}><FaArrowLeft /> Back</Button>
          <Title>Job Details for {job.customer_name}</Title>
          {job.customer_phone && (
            <Button onClick={() => setSmsModalOpen(true)}><FaCommentDots /> Message</Button>
          )}
        </Header>

        {smsModalOpen && (
          <SmsModal
            isOpen={smsModalOpen}
            onClose={() => setSmsModalOpen(false)}
            lead={{
              id: job.customer_id,
              name: job.customer_name,
              phone: job.customer_phone,
              estimates: [],
            }}
            userId={user?.id}
          />
        )}
        <TabWrapper>
          <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
            Overview
          </TabButton>
          <TabButton active={activeTab === 'services'} onClick={() => setActiveTab('services')}>
            Services
          </TabButton>
          <TabButton active={activeTab === 'costs'} onClick={() => setActiveTab('costs')}>
            Costs
          </TabButton>
          <TabButton active={activeTab === 'overview'} onClick={() => history(`/jobs/edit/${job.job_id}`)}>
            Edit
          </TabButton>
        </TabWrapper>

        {activeTab === 'overview' && (
          <>
          <CardWrapper>
            <Card>
              <CardTitle>Customer Information</CardTitle>
              <CardContent>
                <div>Name: {job.customer_name}</div>
                <div>Email: {job.customer_email}</div>
                <div>Phone: {job.customer_phone}</div>
                <div>Address: {`${job.job_address}, ${job.job_city}, ${job.job_state}`}</div>
              </CardContent>
            </Card>
            <Card>
              <CardTitle>Job Status</CardTitle>
              <CardContent>
                <div>Status: {job.status}</div>
                {job.status !== 'Completed' && (
                  <Button onClick={() => handleStatusUpdate('Completed')}>Mark as Completed</Button>
                )}
              </CardContent>
            </Card>
          </CardWrapper>

          {latitude && longitude && !isNaN(latitude) && !isNaN(longitude) && (
            <WeatherForecast
              latitude={latitude}
              longitude={longitude}
              title={`Weather at ${job.job_address || 'Job Site'}`}
            />
          )}
          </>
        )}

        {activeTab === 'services' && (
          <>
          <CardWrapper>
            <Card key="total_price">
              <CardTitle>Total: {formatCurrency(job.total_price)}</CardTitle>
              {job.original_total_price != null
                && Math.abs(parseFloat(job.original_total_price) - parseFloat(job.total_price)) > 0.001 && (
                <CardContent>
                  <div style={{ fontSize: '0.9em', color: '#888' }}>
                    Original: {formatCurrency(job.original_total_price)}
                  </div>
                </CardContent>
              )}
            </Card>
          </CardWrapper>
          <CardWrapper>
            {job.services.map((service, index) => (
              <Card key={index}>
                <CardTitle>Service: {service.service_name}</CardTitle>
                <CardContent>
                  <div>Area: {service.service_area} sqft</div>
                  <div>Price: ${service.service_price}</div>
                </CardContent>
              </Card>
            ))}
          </CardWrapper>
          {estimate && (
            <ChangeOrderListSection
              estimate={estimate}
              estimateId={estimate.id}
              estimateStatus={estimate.status}
              changeOrders={estimate.changeOrders || []}
              services={services}
              onChange={loadJob}
            />
          )}
          </>
        )}

        {activeTab === 'costs' && (
          <>
            {/* Summary Cards */}
            <CostSummaryGrid>
              <SummaryCard accent="#363636">
                <SummaryLabel>Project Value</SummaryLabel>
                <SummaryValue>{formatCurrency(Number(job.total_price))}</SummaryValue>
              </SummaryCard>
              <SummaryCard accent="#dc3545">
                <SummaryLabel>Total Costs</SummaryLabel>
                <SummaryValue color="#dc3545">{formatCurrency(calculateTotalCost())}</SummaryValue>
              </SummaryCard>
              <SummaryCard accent={Number(job.total_price) - parseFloat(calculateTotalCost()) >= 0 ? '#28a745' : '#dc3545'}>
                <SummaryLabel>Profit</SummaryLabel>
                <SummaryValue color={Number(job.total_price) - parseFloat(calculateTotalCost()) >= 0 ? '#28a745' : '#dc3545'}>
                  {formatCurrency(Number(job.total_price) - parseFloat(calculateTotalCost()))}
                </SummaryValue>
              </SummaryCard>
            </CostSummaryGrid>

            <CostBreakdown costs={job.costs || []} costItems={costItems} />

            {/* Add New Costs Form — TOP */}
            {showCostForm && (
              <AddCostSection>
                <CardTitle>Add Costs</CardTitle>
                {costs.map((cost, index) => (
                  <CostEntryCard key={index}>
                    <CostEntryHeader>
                      <CostEntryNum>Cost #{index + 1}</CostEntryNum>
                      <RemoveCostBtn onClick={() => handleRemoveCost(index)}><FaTrash /> Remove</RemoveCostBtn>
                    </CostEntryHeader>

                    <ModeToggleRow>
                      <ModeToggleBtn
                        type="button"
                        active={cost.entryMode !== 'manual'}
                        onClick={() => handleSetEntryMode(index, 'pick')}
                        disabled={costItems.length === 0}
                        title={costItems.length === 0 ? 'No saved items yet — add some from the Cost Items page' : ''}
                      >
                        Pick existing
                      </ModeToggleBtn>
                      <ModeToggleBtn
                        type="button"
                        active={cost.entryMode === 'manual'}
                        onClick={() => handleSetEntryMode(index, 'manual')}
                      >
                        Manual entry
                      </ModeToggleBtn>
                    </ModeToggleRow>

                    {cost.entryMode !== 'manual' ? (
                      <>
                        <Select
                          label="Cost Item"
                          value={cost.cost_item_id || ''}
                          onChange={(e) => handlePickCostItem(index, e.target.value)}
                        >
                          <option value="">Select a saved cost item…</option>
                          {['Crew Hours', 'Fuel', 'Maintenance', 'Materials', 'Products', 'Tools', 'Others'].map((cat) => {
                            const inCat = costItems.filter((it) => it.category === cat);
                            if (inCat.length === 0) return null;
                            return (
                              <optgroup key={cat} label={cat}>
                                {inCat.map((it) => (
                                  <option key={it.cost_item_id} value={it.cost_item_id}>
                                    {it.name}
                                    {it.unit ? ` — $${parseFloat(it.default_unit_price).toFixed(2)}/${it.unit}` : ` — $${parseFloat(it.default_unit_price).toFixed(2)}`}
                                  </option>
                                ))}
                              </optgroup>
                            );
                          })}
                        </Select>
                        {cost.cost_item_id && (
                          <PickerHint>Prefilled from saved item — change quantity below; unit price is editable for one-off overrides.</PickerHint>
                        )}
                      </>
                    ) : (
                      <>
                        <Select
                          label="Category"
                          value={cost.category}
                          onChange={(e) => handleCostChange(index, 'category', e.target.value)}
                        >
                          <option value="">Select Category</option>
                          <option value="Products">Products</option>
                          <option value="Materials">Materials</option>
                          <option value="Crew Hours">Crew Hours</option>
                          <option value="Maintenance">Maintenance</option>
                          <option value="Tools">Tools</option>
                          <option value="Fuel">Fuel</option>
                          <option value="Others">Others</option>
                        </Select>
                        <Input
                          label="Description"
                          type="text"
                          placeholder="What was this cost for?"
                          value={cost.description}
                          onChange={(e) => handleCostChange(index, 'description', e.target.value)}
                        />
                      </>
                    )}

                    <CostEntryRow>
                      <div>
                        <Input
                          label="Quantity"
                          type="number"
                          placeholder="0"
                          value={cost.quantity}
                          onChange={(e) => handleCostChange(index, 'quantity', e.target.value)}
                        />
                      </div>
                      <div>
                        <Input
                          label="Unit Price ($)"
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={cost.unit_price}
                          onChange={(e) => handleCostChange(index, 'unit_price', e.target.value)}
                        />
                      </div>
                    </CostEntryRow>
                    {(cost.quantity && cost.unit_price) && (
                      <CostEntryTotal>
                        Total: {formatCurrency(parseFloat(cost.quantity || 0) * parseFloat(cost.unit_price || 0))}
                      </CostEntryTotal>
                    )}

                    {cost.entryMode === 'manual' && (
                      <ReuseToggleRow>
                        <input
                          type="checkbox"
                          id={`save-template-${index}`}
                          checked={!!cost.saveAsTemplate}
                          onChange={(e) => handleCostChange(index, 'saveAsTemplate', e.target.checked)}
                        />
                        <label htmlFor={`save-template-${index}`} style={{ cursor: 'pointer', fontWeight: 600 }}>
                          Save for reuse
                        </label>
                        {cost.saveAsTemplate && (
                          <>
                            <InlineNameInput
                              type="text"
                              placeholder="Template name (defaults to description)"
                              value={cost.template_name}
                              onChange={(e) => handleCostChange(index, 'template_name', e.target.value)}
                            />
                            <InlineNameInput
                              type="text"
                              placeholder="Unit (hour, mile…)"
                              value={cost.unit}
                              onChange={(e) => handleCostChange(index, 'unit', e.target.value)}
                              style={{ flex: '0 0 140px', minWidth: 0 }}
                            />
                          </>
                        )}
                      </ReuseToggleRow>
                    )}
                  </CostEntryCard>
                ))}
                <ActionBtnRow>
                  <Button type="button" onClick={handleAddCost}><FaPlus /> Add Cost</Button>
                  {costs.length > 0 && (
                    <Button type="button" onClick={handleSaveCosts}><FaSave /> Save All</Button>
                  )}
                </ActionBtnRow>
              </AddCostSection>
            )}

            {/* Existing Costs — BOTTOM */}
            <CardTitle style={{ marginTop: '24px', marginBottom: '12px' }}>Costs Included</CardTitle>
            {job.costs.length > 0 ? (
              job.costs.map((cost, index) => (
                editingCost && editingCost.cost_id === cost.cost_id ? (
                  <CostEntryCard key={cost.cost_id}>
                    <CostEntryHeader>
                      <CostEntryNum>Editing: {cost.description}</CostEntryNum>
                      <RemoveCostBtn onClick={() => setEditingCost(null)}><FaTimes /> Cancel</RemoveCostBtn>
                    </CostEntryHeader>

                    <ModeToggleRow>
                      <ModeToggleBtn
                        type="button"
                        active={editingCost.entryMode !== 'manual'}
                        onClick={() => handleSetEditMode('pick')}
                        disabled={costItems.length === 0}
                      >
                        Pick existing
                      </ModeToggleBtn>
                      <ModeToggleBtn
                        type="button"
                        active={editingCost.entryMode === 'manual'}
                        onClick={() => handleSetEditMode('manual')}
                      >
                        Manual entry
                      </ModeToggleBtn>
                    </ModeToggleRow>

                    {editingCost.entryMode !== 'manual' ? (
                      <>
                        <Select
                          label="Cost Item"
                          value={editingCost.cost_item_id || ''}
                          onChange={(e) => handleEditPickCostItem(e.target.value)}
                        >
                          <option value="">Select a saved cost item…</option>
                          {['Crew Hours', 'Fuel', 'Maintenance', 'Materials', 'Products', 'Tools', 'Others'].map((cat) => {
                            const inCat = costItems.filter((it) => it.category === cat);
                            if (inCat.length === 0) return null;
                            return (
                              <optgroup key={cat} label={cat}>
                                {inCat.map((it) => (
                                  <option key={it.cost_item_id} value={it.cost_item_id}>
                                    {it.name}
                                    {it.unit ? ` — $${parseFloat(it.default_unit_price).toFixed(2)}/${it.unit}` : ` — $${parseFloat(it.default_unit_price).toFixed(2)}`}
                                  </option>
                                ))}
                              </optgroup>
                            );
                          })}
                        </Select>
                        {editingCost.cost_item_id && (
                          <PickerHint>Prefilled from saved item — adjust quantity / unit price below as needed.</PickerHint>
                        )}
                      </>
                    ) : (
                      <>
                        <Select
                          label="Category"
                          value={editingCost.category}
                          onChange={(e) => handleEditCostChange('category', e.target.value)}
                        >
                          <option value="">Select Category</option>
                          <option value="Products">Products</option>
                          <option value="Materials">Materials</option>
                          <option value="Crew Hours">Crew Hours</option>
                          <option value="Maintenance">Maintenance</option>
                          <option value="Tools">Tools</option>
                          <option value="Fuel">Fuel</option>
                          <option value="Others">Others</option>
                        </Select>
                        <Input
                          label="Description"
                          type="text"
                          value={editingCost.description}
                          onChange={(e) => handleEditCostChange('description', e.target.value)}
                        />
                      </>
                    )}

                    <CostEntryRow>
                      <div>
                        <Input
                          label="Quantity"
                          type="number"
                          value={editingCost.quantity}
                          onChange={(e) => handleEditCostChange('quantity', e.target.value)}
                        />
                      </div>
                      <div>
                        <Input
                          label="Unit Price ($)"
                          type="number"
                          step="0.01"
                          value={editingCost.unit_price}
                          onChange={(e) => handleEditCostChange('unit_price', e.target.value)}
                        />
                      </div>
                    </CostEntryRow>
                    <CostEntryTotal>
                      Total: {formatCurrency(parseFloat(editingCost.quantity || 0) * parseFloat(editingCost.unit_price || 0))}
                    </CostEntryTotal>

                    {editingCost.entryMode === 'manual' && (
                      <ReuseToggleRow>
                        <input
                          type="checkbox"
                          id="edit-save-template"
                          checked={!!editingCost.saveAsTemplate}
                          onChange={(e) => handleEditCostChange('saveAsTemplate', e.target.checked)}
                        />
                        <label htmlFor="edit-save-template" style={{ cursor: 'pointer', fontWeight: 600 }}>
                          Save for reuse
                        </label>
                        {editingCost.saveAsTemplate && (
                          <>
                            <InlineNameInput
                              type="text"
                              placeholder="Template name (defaults to description)"
                              value={editingCost.template_name || ''}
                              onChange={(e) => handleEditCostChange('template_name', e.target.value)}
                            />
                            <InlineNameInput
                              type="text"
                              placeholder="Unit (hour, mile…)"
                              value={editingCost.unit || ''}
                              onChange={(e) => handleEditCostChange('unit', e.target.value)}
                              style={{ flex: '0 0 140px', minWidth: 0 }}
                            />
                          </>
                        )}
                      </ReuseToggleRow>
                    )}

                    <ActionBtnRow>
                      <Button type="button" onClick={handleSaveEditCost}><FaSave /> Save</Button>
                    </ActionBtnRow>
                  </CostEntryCard>
                ) : (
                  <ExistingCostCard key={cost.cost_id || index}>
                    <CostCardInfo>
                      <CostCardTitle>{cost.description}</CostCardTitle>
                      <CostCardSub>{cost.category} · Qty: {cost.quantity} × {formatCurrency(cost.unit_price)}</CostCardSub>
                    </CostCardInfo>
                    <CostCardAmount>{formatCurrency(cost.total_cost)}</CostCardAmount>
                    <CostActionBtns>
                      <CostIconBtn onClick={() => handleEditCost(cost)} title="Edit"><FaEdit /></CostIconBtn>
                      <CostIconBtn color="#dc3545" onClick={() => handleDeleteCost(cost)} title="Delete"><FaTrash /></CostIconBtn>
                    </CostActionBtns>
                  </ExistingCostCard>
                )
              ))
            ) : (
              <div style={{ color: '#888', padding: '16px 0', textAlign: 'center' }}>No costs recorded yet.</div>
            )}
          </>
        )}

        {activeTab === 'overview' && (
          <CardWrapper>
            <Card>
              <CardTitle>Nearby Suppliers</CardTitle>
              <MapWrapper>
                    {latitude && longitude && (
                        <Map
                          addresses={[
                            {
                            lat: latitude,
                            lng: longitude,
                            title: 'Job Site', // Title for job site pin
                            isSupplier: false, // This pin represents the job site
                            },
                            ...formattedSuppliers, //
                          ]}
                        jobSitePin={jobSitePinUrl} // Red pin for job site
                        supplierPin={supplierPinUrl} // Blue pin for suppliers
                        />
                    )}
                </MapWrapper>
                <div>
                {suppliers.length > 0 ? (
                  suppliers.map((supplier) => (
                    <SupplierItem key={supplier.id}>
                      <SupplierDetails>
                        <SupplierName>{supplier.name}</SupplierName>
                        <SupplierDistance>{supplier.distance} miles away</SupplierDistance>
                        <SupplierCategories>
                          Categories: {supplier.categories.length > 0 ? supplier.categories.map((category) => category.name).join(', ') : 'No categories available'}
                        </SupplierCategories>
                      </SupplierDetails>
                    </SupplierItem>
                  ))
                ) : (
                  <div>No suppliers found within the specified radius.</div>
                )}
              </div>
            </Card>
          </CardWrapper>
        )}
      </Container>
      <>
    <Modal
    isOpen={deleteModalOpen}
    onClose={() => setDeleteModalOpen(false)}
    onCancel={() => setDeleteModalOpen(false)}
    onConfirm={confirmDeleteCost}
    title="Confirm Deletion"
  >
    <p>Are you sure you want to delete this cost?</p>
    {costToDelete && (
      <div>
        <p>Category: {costToDelete.category}</p>
        <p>Description: {costToDelete.description}</p>
        <p>Total Cost: ${costToDelete.total_cost}</p>
      </div>
    )}
  </Modal>
  </>
    </DashboardLayout>
  );
};

export default JobDetails;
